import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorFallback } from './ErrorFallback';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional callback for logging/telemetry when an error is caught. */
  onError?: (error: Error, info: ErrorInfo) => void;
  /** Custom fallback renderer. When omitted, the branded default is shown. */
  fallback?: (args: { error: Error; reset: () => void }) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * ErrorBoundary — catches render/runtime errors in its subtree and shows a
 * premium, brand-themed recovery screen instead of a blank white page.
 *
 * Router-free by design so it can wrap both the routed clinic app and the
 * router-less admin panel. Presentational only — logging is delegated to `onError`.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onError?.(error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) {
      return this.props.fallback({ error, reset: this.reset });
    }

    return <ErrorFallback error={error} reset={this.reset} />;
  }
}
